import { createContext, useContext, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IAction } from "~/components/shared/add-action-modal/types";
import { ACTION_REGISTRY, ActionButtonDef } from "./registry";
import FillWorkHoursModal from "~/components/pages/recordManagement/work-hours-register/fill-work-hours-modal";
import AuditExportPreview from "~/components/pages/itemManagement/audits/details/export-preview";
import InspectionExportPreview from "~/components/pages/itemManagement/inspections/details/export-preview";
import { ActionWorkspaceModal } from "./action-workspace-modal";

interface ActionHandlerContextType {
  getActionButtons: (action: IAction) => ActionButtonDef[];
  handleActionClick: (action: IAction, def: ActionButtonDef) => void;
  workHoursTaskToComplete: IAction | null;
  setWorkHoursTaskToComplete: (action: IAction | null) => void;
  workspaceAction: IAction | null;
  setWorkspaceAction: (action: IAction | null) => void;
}

const ActionHandlerContext = createContext<
  ActionHandlerContextType | undefined
>(undefined);

export const ActionHandlerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const userId = useSelector(
    (state: any) => state.persistedReducer.auth.userId,
  );

  const [workHoursTaskToComplete, setWorkHoursTaskToComplete] =
    useState<IAction | null>(null);

  const [workHoursTaskToView, setWorkHoursTaskToView] =
    useState<IAction | null>(null);

  const [auditIdToView, setAuditIdToView] = useState<string | null>(null);
  const [inspectionIdToView, setInspectionIdToView] = useState<string | null>(
    null,
  );

  const [workspaceAction, setWorkspaceAction] = useState<IAction | null>(null);

  const getActionButtons = (action: IAction): ActionButtonDef[] => {
    const isWHR =
      action.modelType === "WorkHoursRegister" ||
      action.model?.reference?.startsWith("WHR") ||
      action.reference?.startsWith("WHR");

    const isCapa =
      action.category === "CAPA" ||
      action.type === "CorrectiveAction" ||
      action.type === "CAPA";

    const isAudit =
      action.modelType === "Audit" ||
      action.type === "AuditAssignment" ||
      !!(action as any).auditId ||
      action.model?.reference?.startsWith("AUD") ||
      action.reference?.startsWith("AUD");

    const isInspection =
      action.modelType === "Inspection" ||
      action.type === "InspectionAssignment" ||
      !!(action as any).inspectionId ||
      action.model?.reference?.startsWith("INS") ||
      action.reference?.startsWith("INS");

    if (!isWHR && !isCapa && !isAudit && !isInspection) {
      return [];
    }

    const isAssignedToMe = action.responsiblePerson?.id === userId;
    const isNotCompleted =
      action.status?.name !== "Closed" &&
      action.status?.name !== "Ready for Review" &&
      action.status?.name !== "Voided";

    // We can assume full access to a module if they have specific permissions,
    // but for now we default to false or standard check.
    const hasFullAccess = true;

    let typeValue = action.type;

    if (isWHR && (!typeValue || typeValue === "WorkHoursTask")) {
      typeValue = "LogHours";
    } else if (isCapa) {
      typeValue = "CorrectiveAction";
    } else if (
      isAudit &&
      (!typeValue ||
        typeValue === "Task" ||
        typeValue === "GeneralTask" ||
        typeValue === "AuditAssignment")
    ) {
      typeValue = "AuditTask";
    } else if (
      isInspection &&
      (!typeValue ||
        typeValue === "Task" ||
        typeValue === "GeneralTask" ||
        typeValue === "InspectionAssignment")
    ) {
      typeValue = "InspectionTask";
    }

    // Find the registry entry
    const entry = Object.values(ACTION_REGISTRY).find(
      (r) => r.value === typeValue,
    );

    if (!entry) {
      return [];
    }

    return entry.getButtons(action, {
      isAssignedToMe,
      isNotCompleted,
      hasFullAccess,
    });
  };

  const handleActionClick = (action: IAction, def: ActionButtonDef) => {
    if (def.onClick) {
      def.onClick(action);
      return;
    }

    if (def.actionType === "navigate" && def.route) {
      navigate(def.route);
    } else if (def.actionType === "navigate_new_tab" && def.route) {
      window.open(def.route, "_blank");
    } else if (def.actionType === "modal") {
      if (def.modalType === "fillWorkHours") {
        setWorkHoursTaskToComplete(action);
      } else if (def.modalType === "viewWorkHours") {
        setWorkHoursTaskToView(action);
      } else if (def.modalType === "viewAuditPdf") {
        const auditId =
          action.model?.id ||
          (action as any).modelId ||
          (action as any).auditId ||
          "";
        setAuditIdToView(auditId);
      } else if (def.modalType === "viewInspectionPdf") {
        const inspectionId =
          action.model?.id ||
          (action as any).modelId ||
          (action as any).inspectionId ||
          "";
        setInspectionIdToView(inspectionId);
      } else if (def.modalType === "workspaceAction") {
        setWorkspaceAction(action);
      }
    }
  };

  return (
    <ActionHandlerContext.Provider
      value={{
        getActionButtons,
        handleActionClick,
        workHoursTaskToComplete,
        setWorkHoursTaskToComplete,
        workspaceAction,
        setWorkspaceAction,
      }}
    >
      {children}

      {/* Render dialogs centrally here so they exist exactly once in the DOM */}
      {workHoursTaskToComplete && (
        <FillWorkHoursModal
          isOpen={!!workHoursTaskToComplete}
          onClose={() => setWorkHoursTaskToComplete(null)}
          action={workHoursTaskToComplete}
          onSuccess={() => {
            setWorkHoursTaskToComplete(null);
          }}
        />
      )}

      {workHoursTaskToView && (
        <FillWorkHoursModal
          isOpen={!!workHoursTaskToView}
          onClose={() => setWorkHoursTaskToView(null)}
          action={workHoursTaskToView}
          viewOnly
        />
      )}

      {auditIdToView && (
        <AuditExportPreview
          isOpen={!!auditIdToView}
          onClose={() => setAuditIdToView(null)}
          auditId={auditIdToView}
        />
      )}

      {inspectionIdToView && (
        <InspectionExportPreview
          isOpen={!!inspectionIdToView}
          onClose={() => setInspectionIdToView(null)}
          inspectionId={inspectionIdToView}
        />
      )}

      {workspaceAction && (
        <ActionWorkspaceModal
          isOpen={!!workspaceAction}
          onClose={() => setWorkspaceAction(null)}
          action={workspaceAction}
        />
      )}
    </ActionHandlerContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useActionHandler = () => {
  const context = useContext(ActionHandlerContext);
  if (context === undefined) {
    throw new Error(
      "useActionHandler must be used within an ActionHandlerProvider",
    );
  }
  return context;
};
